import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, Trash2 } from "lucide-react";

export default function MemberDetail() {
  const { phone } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    const r = await api.get(`/admin/members/${encodeURIComponent(phone)}`);
    setM(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phone]);

  const adjust = async () => {
    if (!delta) return;
    setSaving(true);
    try {
      await api.post(`/admin/members/${encodeURIComponent(phone)}/adjust`, { points_delta: Number(delta), reason: reason || "manual" });
      setDelta(""); setReason("");
      await load();
    } finally { setSaving(false); }
  };

  const deleteMember = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/members/${encodeURIComponent(phone)}`);
      nav("/pos/members");
    } catch {
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  if (!m) return <Layout><div className="max-w-4xl mx-auto px-6 py-16"><Loader2 className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => nav("/pos/members")} className="label-eyebrow mb-6 inline-block" data-testid="md-back">← Back to Members</button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>AB Member</div>
            <h1 className="font-display text-4xl font-black mb-2" data-testid="md-name">{m.first_name} {m.last_name}</h1>
            <div className="text-sm mb-8" style={{ color: "var(--ab-text-muted)" }}>{m.phone} · {m.email}</div>
          </div>
          <button onClick={() => setConfirmDel(true)} className="btn-danger inline-flex items-center gap-2" data-testid="md-delete-btn">
            <Trash2 size={14} /> Delete Member
          </button>
        </div>

        <div className="grid sm:grid-cols-5 gap-3 mb-8">
          <Stat label="µPoints" value={Number(m.points).toLocaleString()} highlight />
          <Stat label="Trade Credit" value={`$${Number(m.trade_credit || 0).toLocaleString()}`} highlight />
          <Stat label="Lifetime $" value={`$${Number(m.spend_lifetime).toFixed(2)}`} />
          <Stat label="Last 72h $" value={`$${Number(m.spend_72h).toFixed(2)}`} />
          <Stat label="This Month $" value={`$${Number(m.spend_month).toFixed(2)}`} />
        </div>

        <div className="surface p-5 mb-8">
          <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Adjust µPoints</div>
          <div className="flex flex-wrap gap-2">
            <input type="number" className="input-terminal" style={{ width: 140 }} value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+/- pts" data-testid="md-delta" />
            <input className="input-terminal" style={{ width: 240 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="md-reason" />
            <button onClick={adjust} disabled={saving || !delta} className="btn-primary" data-testid="md-apply">{saving ? <Loader2 size={14} className="animate-spin" /> : "Apply"}</button>
          </div>
        </div>

        <TradeCreditAdjuster phone={phone} onSaved={load} />

        <h3 className="label-eyebrow mb-3">Trade Credit Activity</h3>
        <div className="surface divide-y mb-8" style={{ borderColor: "var(--ab-border)" }} data-testid="md-tc-timeline">
          {((m.trade_credit_adjustments || []).length === 0) && (
            <div className="p-5 text-sm" style={{ color: "var(--ab-text-muted)" }}>No trade credit activity yet.</div>
          )}
          {[...(m.trade_credit_adjustments || [])].reverse().map((adj, idx) => (
            <div key={idx} className="px-5 py-3 grid grid-cols-12 gap-2 text-sm items-center" data-testid={`md-tc-row-${idx}`}>
              <div className="font-mono text-xs col-span-4" style={{ color: "var(--ab-text-muted)" }}>{adj.at?.replace("T", " ").slice(0, 16)}</div>
              <div className="col-span-6 truncate" style={{ color: "var(--ab-text)" }}>{adj.reason || "—"}</div>
              <div className="col-span-2 font-mono text-right font-bold" style={{ color: adj.delta >= 0 ? "var(--ab-primary)" : "var(--ab-danger)" }}>
                {adj.delta >= 0 ? "+" : ""}${Number(adj.delta).toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        <h3 className="label-eyebrow mb-3">Recent Transactions</h3>
        <div className="surface divide-y" style={{ borderColor: "var(--ab-border)" }}>
          {(m.recent_transactions || []).length === 0 && <div className="p-5 text-sm" style={{ color: "var(--ab-text-muted)" }}>No transactions yet.</div>}
          {(m.recent_transactions || []).map((t) => (
            <div key={t.id} className="px-5 py-3 grid grid-cols-5 gap-2 text-sm">
              <div className="font-mono text-xs col-span-2" style={{ color: "var(--ab-text-muted)" }}>{t.completed_at}</div>
              <div className="font-mono text-right">${Number(t.total).toFixed(2)}</div>
              <div className="font-mono text-right" style={{ color: "var(--ab-primary)" }}>+{t.points_earned}</div>
              <div className="font-mono text-right" style={{ color: "var(--ab-danger)" }}>-{t.points_redeemed}</div>
            </div>
          ))}
        </div>

        {confirmDel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => !deleting && setConfirmDel(false)}>
            <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="md-delete-modal">
              <h3 className="font-display text-2xl font-bold mb-2">Delete {m.first_name} {m.last_name}?</h3>
              <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>
                This permanently removes the member and all of their transaction history. Sales records remain in the system but lose their member link. This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDel(false)} disabled={deleting} className="btn-secondary" data-testid="md-delete-cancel">Cancel</button>
                <button onClick={deleteMember} disabled={deleting} className="btn-danger inline-flex items-center gap-2" data-testid="md-delete-confirm">
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Permanently Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="surface p-4">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className={"font-mono font-bold " + (highlight ? "text-3xl" : "text-2xl")} style={{ color: highlight ? "var(--ab-primary)" : "var(--ab-text)" }}>{value}</div>
    </div>
  );
}

function TradeCreditAdjuster({ phone, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const apply = async () => {
    if (!delta) return;
    setSaving(true);
    try {
      await api.post(`/admin/members/${encodeURIComponent(phone)}/trade-credit`, { amount_delta: Number(delta), reason: reason || "manual" });
      setDelta(""); setReason("");
      onSaved?.();
    } finally { setSaving(false); }
  };
  return (
    <div className="surface p-5 mb-8">
      <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Adjust Trade Credit ($)</div>
      <div className="flex flex-wrap gap-2">
        <input type="number" step="1" className="input-terminal" style={{ width: 140 }} value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+/- $" data-testid="md-tc-delta" />
        <input className="input-terminal" style={{ width: 240 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="md-tc-reason" />
        <button onClick={apply} disabled={saving || !delta} className="btn-primary" data-testid="md-tc-apply">{saving ? <Loader2 size={14} className="animate-spin" /> : "Apply"}</button>
      </div>
    </div>
  );
}
